import { useEffect, useState } from "react";
import { Search, ShoppingCart } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { resolveImage } from "@/lib/image";

export default function Grocery() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const { addItem } = useCart();

  useEffect(() => {
    api.get("/products").then(({ data }) => setItems(data)).catch(() => {});
    api.get("/products/categories").then(({ data }) => setCats(["All", ...data])).catch(() => {});
  }, []);

  const filtered = items.filter((i) =>
    (filter === "All" || i.category === filter) &&
    (q === "" || i.name.toLowerCase().includes(q.toLowerCase()))
  );

  const add = (p) => {
    addItem({ item_type: "grocery", item_id: p.id, name: p.name, unit_price: p.price, image: p.image });
    toast.success(`${p.name} added`);
  };

  return (
    <div className="bg-[#FAF9F6] min-h-screen" data-testid="grocery-page">
      <section className="bg-gradient-to-br from-[#2C4A3B] to-[#1E3329] text-[#FAF9F6] py-14">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <BrandLogo type="grocery" size="lg" icon="leaf" showText={false} className="justify-center mb-3" />
          <h1 className="font-heading text-5xl lg:text-6xl mb-2">Maharlika <span className="italic text-[#D4AF37]">Mart</span></h1>
          <p className="text-[#FAF9F6]/80 max-w-xl">Fresh Filipino & Asian groceries. Daily seafood catches. Pantry staples for every adobo, sinigang, and pancit you'll ever make.</p>
        </div>
      </section>

      <section className="border-b border-[#8A9A86]/20 bg-[#FAF9F6] sticky top-[64px] z-30">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4 flex flex-wrap gap-3 items-center justify-between">
          <div className="flex gap-2 overflow-x-auto no-scrollbar flex-1">
            {cats.map((c) => (
              <button
                key={c}
                data-testid={`grocery-cat-${c.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => setFilter(c)}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-xs font-medium tracking-wide transition-colors ${
                  filter === c ? "bg-[#2C4A3B] text-[#FAF9F6]" : "text-[#2C4A3B] border border-[#2C4A3B]/30 hover:bg-[#2C4A3B]/5"
                }`}
              >{c}</button>
            ))}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-[#2C4A3B] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              data-testid="grocery-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products..."
              className="border border-[#8A9A86]/40 text-[#1A2421] pl-9 pr-3 py-2 text-sm rounded-full focus:outline-none focus:border-[#2C4A3B]"
            />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
        {filtered.map((p) => (
          <article key={p.id} className="mart-card group" data-testid={`product-${p.id}`}>
            <div className="aspect-square bg-[#F2EFEB] bg-cover bg-center" style={{ backgroundImage: `url(${resolveImage(p.image)})` }} />
            <div className="p-3">
              <h3 className="font-heading text-base text-[#1A2421] leading-tight line-clamp-2 min-h-[44px]">{p.name}</h3>
              <div className="flex items-center gap-1.5 mt-1.5 mb-3 flex-wrap">
                {p.fresh_today && <span className="badge-fresh">FRESH</span>}
                {p.stock <= p.low_stock_threshold && p.stock > 0 && <span className="text-[10px] text-orange-700 font-medium">Low stock</span>}
                {p.stock === 0 && <span className="text-[10px] text-red-700 font-medium">Sold out</span>}
              </div>
              <div className="flex items-center justify-between">
                <span className="font-heading text-lg text-[#2C4A3B] font-semibold">${p.price.toFixed(2)}<span className="text-xs text-[#5C6B64] font-normal">/{p.unit}</span></span>
                <button
                  data-testid={`product-add-${p.id}`}
                  disabled={p.stock === 0}
                  onClick={() => add(p)}
                  className="w-9 h-9 rounded-full bg-[#2C4A3B] text-[#FAF9F6] hover:bg-[#1E3329] disabled:opacity-30 flex items-center justify-center transition-colors"
                >
                  <ShoppingCart className="w-4 h-4" />
                </button>
              </div>
            </div>
          </article>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16 text-[#5C6B64]">No products found.</div>
        )}
      </section>
    </div>
  );
}
